import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaCheck,
  FaCheckDouble,
  FaTimes,
  FaTrash,
  FaBullhorn,
  FaUsers,
  FaCalendarCheck,
  FaInfoCircle,
  FaExclamationTriangle,
  FaProjectDiagram,
  FaHome,
  FaFileInvoiceDollar,
  FaReceipt,
  FaClipboardCheck,
  FaStickyNote,
  FaShieldAlt,
  FaConciergeBell,
  FaUserCog
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import logger from '../../utils/logger';
import { showNotification } from '../../utils/notifications';
import { useOverlayBack } from '../../hooks/useSmartBack';
import './NotificationPanel.scss';

const MOBILE_PANEL_QUERY = '(max-width: 767px)';

const ResponsivePanelPortal = ({ isMobile, children }) => (
  isMobile && typeof document !== 'undefined'
    ? createPortal(children, document.body)
    : children
);

// Icon map — aligned exactly with Sidebar icons for instant visual match
const NOTIFICATION_ICONS = {
  announcement: FaBullhorn,
  payment: FaReceipt, // sidebar billing = FaReceipt (was FaCreditCard)
  billing: FaReceipt,
  team: FaUsers,
  event: FaCalendarCheck, // sidebar events = FaCalendarCheck (was FaCalendarAlt)
  project: FaProjectDiagram,
  system: FaInfoCircle,
  plan: FaExclamationTriangle,
  security: FaShieldAlt,
  info: FaInfoCircle,
  success: FaCheck,
  warning: FaExclamationTriangle,
  error: FaTimes,
  quotation: FaFileInvoiceDollar, // sidebar quotations = FaFileInvoiceDollar
  checklist: FaClipboardCheck, // sidebar checklists
  checklists: FaClipboardCheck,
  note: FaStickyNote, // sidebar notes
  notes: FaStickyNote,
  policy: FaShieldAlt, // sidebar policy
  service: FaConciergeBell, // sidebar services
  services: FaConciergeBell,
  dashboard: FaHome,
  admin: FaUserCog,
};

/**
 * NotificationPanel Component
 * 
 * A dropdown panel that shows user notifications.
 * Includes bell icon with unread badge.
 */
const NotificationPanel = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(MOBILE_PANEL_QUERY).matches
  ));
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const lastCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);
  const seenToastIdsRef = useRef(new Set());
  const isFetchingRef = useRef(false);
  const backoffUntilRef = useRef(0);

  const extractUnreadCount = (payload) => {
    if (typeof payload?.count === 'number') return payload.count;
    if (typeof payload?.data?.count === 'number') return payload.data.count;
    return 0;
  };

  const extractNotificationList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const rememberToastId = (id) => {
    if (!id) return;
    seenToastIdsRef.current.add(String(id));
    // Keep bounded memory footprint in long sessions
    if (seenToastIdsRef.current.size > 200) {
      const firstKey = seenToastIdsRef.current.values().next().value;
      seenToastIdsRef.current.delete(firstKey);
    }
  };

  const showToastsForNewUnread = async () => {
    const latestData = await apiHelper.get('/notifications?limit=10&unreadOnly=true');
    const latestList = extractNotificationList(latestData);
    if (!latestList.length) return;

    const newestFirst = [...latestList];
    const unseen = newestFirst
      .filter((item) => item && item._id && !seenToastIdsRef.current.has(String(item._id)))
      .reverse();

    unseen.forEach((latest) => {
      showNotification(latest.message, {
        title: latest.title,
        type: latest.type === 'error' ? 'error' : (latest.type === 'warning' ? 'warning' : 'info'),
        duration: 5000,
      });
      rememberToastId(latest._id);

      // Signal AnnouncementBanner to re-fetch and show the new announcement dialog
      if (latest.type === 'announcement') {
        window.dispatchEvent(
          new CustomEvent('ui:new-announcement', {
            detail: { referenceId: latest.referenceId }
          })
        );
      }
    });
  };

  // Fetch unread count — deduplicated + 429 backoff
  const fetchUnreadCount = useCallback(async () => {
    if (isFetchingRef.current) return;
    if (Date.now() < backoffUntilRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    isFetchingRef.current = true;
    try {
      const data = await apiHelper.get('/notifications/unread-count');
      const newCount = extractUnreadCount(data);

      // If count increased, fetch latest notification and show toast
      if (!isFirstLoadRef.current && newCount > lastCountRef.current) {
        try {
          await showToastsForNewUnread();
        } catch (err) {
          // Skip toast fetch errors unless they are rate-limit
          const s = err?.response?.status;
          if (s === 429) {
            const retryAfter = Number(err?.response?.headers?.['retry-after'] || 60);
            backoffUntilRef.current = Date.now() + Math.min(retryAfter * 1000, 120000);
          }
          logger.debug('Error fetching latest notification for toast', err);
        }
      }

      if (isFirstLoadRef.current) {
        try {
          const seedData = await apiHelper.get('/notifications?limit=10&unreadOnly=true');
          const seedList = extractNotificationList(seedData);
          seedList.forEach((item) => rememberToastId(item?._id));
        } catch (err) {
          const s = err?.response?.status;
          if (s === 429) {
            const retryAfter = Number(err?.response?.headers?.['retry-after'] || 60);
            backoffUntilRef.current = Date.now() + Math.min(retryAfter * 1000, 120000);
          }
          logger.debug('Error seeding unread notification ids', err);
        }
      }

      setUnreadCount(newCount);
      lastCountRef.current = newCount;
      isFirstLoadRef.current = false;
      backoffUntilRef.current = 0;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        const retryAfter = Number(error?.response?.headers?.['retry-after'] || 60);
        backoffUntilRef.current = Date.now() + Math.min(retryAfter * 1000, 120000);
        logger.debug('Notifications unread-count rate-limited, backing off', error, 'Notifications');
      } else {
        logger.debug('Error fetching unread count', error, 'Notifications');
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = filter !== 'all' ? `?type=${filter}` : '';
      const data = (await apiHelper.get(`/notifications${params}`)) || [];
      const list = Array.isArray(data) ? data : (data?.data || []);
      setNotifications(list);

      // Update count contextually
      const count = typeof data?.unreadCount !== 'undefined' ? data.unreadCount : (list.filter(n => !n.read).length);
      setUnreadCount(count);
      lastCountRef.current = count;
    } catch (error) {
      logger.debug('Error fetching notifications', error, 'Notifications');
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  // Initial fetch of unread count — poll every 20s + instant refresh on demand (pause when tab hidden)
  // NOTE: was 60s. Push is delivered even while online, and push-receiving
  // tabs refresh instantly via the SW postMessage below — 20s polling keeps
  // the badge snappy as a fallback while staying far under the per-user
  // rate limit (~45 req/15min).
  useEffect(() => {
    fetchUnreadCount();

    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchUnreadCount();
    }, 20000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Refresh when window regains focus
    window.addEventListener('focus', fetchUnreadCount);
    // Instant refresh trigger fired after quotation/event/billing creation
    window.addEventListener('notifications:refresh', fetchUnreadCount);
    // ASAP: service worker posts this when a push lands on an open tab —
    // refresh the badge + toasts immediately instead of waiting for next poll
    let swMessageHandler = null;
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      swMessageHandler = (event) => {
        if (event?.data?.type === 'MIVENT_PUSH_RECEIVED') {
          fetchUnreadCount();
          if (isOpen) fetchNotifications();
        }
      };
      try {
        navigator.serviceWorker.addEventListener('message', swMessageHandler);
      } catch (e) { /* older browsers */ }
    }

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', fetchUnreadCount);
      window.removeEventListener('notifications:refresh', fetchUnreadCount);
      if (swMessageHandler) {
        try {
          navigator.serviceWorker.removeEventListener('message', swMessageHandler);
        } catch (e) {}
      }
    };
  }, [fetchUnreadCount, isOpen, fetchNotifications]);

  // Fetch notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, filter, fetchNotifications]);

  // Keep the mobile panel in a portal so navbar backdrop filters cannot
  // constrain fixed positioning to the navbar instead of the viewport.
  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_PANEL_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);

    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Step-by-step back: a back press with the panel open closes the panel
  // first instead of leaving the page. Manual dismissal unwinds the guard.
  const closePanel = useCallback(() => setIsOpen(false), []);
  useOverlayBack(isOpen, closePanel, 'notifications');

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedPanel = panelRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedPanel) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Mark single as read
  const handleMarkAsRead = async (id) => {
    try {
      await apiHelper.post(`/notifications/${id}/read`);
      setNotifications(prev => 
        prev.map(n => n._id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        lastCountRef.current = next;
        return next;
      });
    } catch (error) {
      logger.debug('Error marking as read', error, 'Notifications');
    }
  };

  // Map notification type / referenceModel to frontend route
  const getRouteForNotification = (notification) => {
    const type = String(notification?.type || '').toLowerCase();
    const model = String(notification?.referenceModel || '').toLowerCase();
    const key = type || model;
    if (key === 'quotation' || model === 'quotation') return '/quotations';
    if (key === 'event' || model === 'event') return '/events';
    if (key === 'payment' || model === 'payment' || type === 'payment') return '/billing';
    if (key === 'project' || model === 'project') return '/projects';
    if (key === 'team' || model === 'teammember') return '/team';
    if (key === 'announcement') return null;
    return null;
  };

  // Handle notification item click
  const handleItemClick = (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }

    // Announcement notifications open the SAME global AnnouncementModal
    // used for fresh announcements (full content: text and/or image/video).
    // The modal lives at the app root so it is never clipped by the navbar's
    // stacking context — unlike the old inline panel dialog.
    if (notification.type === 'announcement') {
      const rawRef = notification.referenceId;
      let refId = rawRef && typeof rawRef === 'object'
        ? (rawRef._id || rawRef.$oid || null)
        : rawRef;
      if (refId && typeof refId === 'object') {
        try { refId = String(refId); } catch (e) { refId = null; }
      }
      // Close the dropdown first so the modal is fully visible above it.
      setIsOpen(false);
      if (refId) {
        window.dispatchEvent(
          new CustomEvent('ui:open-announcement', {
            detail: { announcementId: String(refId) },
          })
        );
      } else {
        // No reference — still open the same modal with the panel snippet.
        window.dispatchEvent(
          new CustomEvent('ui:open-announcement', {
            detail: {
              announcement: {
                title: notification.title,
                content: notification.message,
                theme: 'default',
                displayOptions: { dismissible: true, showAsModal: true },
              },
            },
          })
        );
      }
      return;
    }

    // Derive navigation target: prefer actionUrl, fallback to referenceId + type
    let targetPath = null;
    let targetId = null;

    if (notification.actionUrl) {
      const [path, query] = notification.actionUrl.split('?');
      targetPath = path;
      const params = new URLSearchParams(query || '');
      targetId = params.get('id');
      // Fallback: if actionUrl has no id but notification has referenceId, use it
      if (!targetId && notification.referenceId) {
        targetId = String(notification.referenceId);
      }
      // Normalize project reminder legacy url "/projects" without id
      if (!targetId && targetPath === '/projects' && notification.referenceId) {
        targetId = String(notification.referenceId);
      }
    } else {
      // No actionUrl — try to resolve via type / referenceModel
      targetPath = getRouteForNotification(notification);
      if (targetPath && notification.referenceId) {
        targetId = String(notification.referenceId);
      }
    }

    if (targetPath) {
      // Navigate with state so target page can open via location.state
      // also dispatch ui:notification for when user is already on that page
      try {
        navigate(targetPath, { state: { openViewId: targetId } });
      } catch (e) {
        navigate(targetPath);
      }

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ui:notification', {
          detail: {
            path: targetPath,
            state: { openViewId: targetId }
          }
        }));
      }, 150);

      setIsOpen(false);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await apiHelper.post('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      lastCountRef.current = 0;
    } catch (error) {
      logger.debug('Error marking all as read', error, 'Notifications');
    }
  };

  // Dismiss notification
  const handleDismiss = async (id) => {
    try {
      await apiHelper.post(`/notifications/${id}/dismiss`);
      setNotifications(prev => prev.filter(n => n._id !== id));
    } catch (error) {
      logger.debug('Error dismissing notification', error, 'Notifications');
    }
  };

  // Clear all notifications (dismiss each and remove from UI)
  const handleClearAll = async () => {
    if (!notifications || notifications.length === 0) return;
    setIsClearing(true);
    try {
      const ids = notifications.map(n => n._id);
      await Promise.all(ids.map(id => apiHelper.post(`/notifications/${id}/dismiss`)));
      setNotifications([]);
      setUnreadCount(0);
      lastCountRef.current = 0;
      showNotification('All notifications cleared', { type: 'success', duration: 2500 });
    } catch (error) {
      logger.debug('Error clearing notifications', error, 'Notifications');
      showNotification('Failed to clear notifications', { type: 'error' });
    } finally {
      setIsClearing(false);
    }
  };

  // Format relative time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get icon component — matches Sidebar icons via actionUrl > type > icon
  const getIcon = (notification) => {
    const actionUrl = String(notification.actionUrl || '').toLowerCase();
    const typeKey = String(notification.type || '').toLowerCase();
    const modelKey = String(notification.referenceModel || '').toLowerCase();
    const iconKey = String(notification.icon || '').toLowerCase();

    // 1) Derive from actionUrl (most reliable for system-type checklists/notes/policy/service)
    if (actionUrl.includes('/quotations')) return <FaFileInvoiceDollar />;
    if (actionUrl.includes('/events')) return <FaCalendarCheck />;
    if (actionUrl.includes('/billing') || actionUrl.includes('/payment')) return <FaReceipt />;
    if (actionUrl.includes('/checklists')) return <FaClipboardCheck />;
    if (actionUrl.includes('/notes')) return <FaStickyNote />;
    if (actionUrl.includes('/team')) return <FaUsers />;
    if (actionUrl.includes('/projects')) return <FaProjectDiagram />;
    if (actionUrl.includes('/policy')) return <FaShieldAlt />;
    if (actionUrl.includes('/services')) return <FaConciergeBell />;
    if (actionUrl.includes('/dashboard')) return <FaHome />;
    if (actionUrl.includes('/admin')) return <FaUserCog />;

    // 2) Fallback to explicit type/model/icon map (now sidebar-aligned)
    const directKey = iconKey || typeKey || modelKey;
    // normalize common aliases
    const aliasMap = {
      quotation: 'quotation',
      quotations: 'quotation',
      billing: 'billing',
      payment: 'payment',
      event: 'event',
      events: 'event',
      project: 'project',
      projects: 'project',
      team: 'team',
      checklist: 'checklist',
      checklists: 'checklist',
      note: 'note',
      notes: 'note',
      policy: 'policy',
      service: 'service',
      services: 'service',
    };
    const normalized = aliasMap[directKey] || directKey;
    const IconComponent = NOTIFICATION_ICONS[normalized] || NOTIFICATION_ICONS[iconKey] || NOTIFICATION_ICONS[typeKey] || FaBell;
    return <IconComponent />;
  };

  const orderedNotifications = [
    ...notifications.filter(notification => !notification.read),
    ...notifications.filter(notification => notification.read),
  ];

  return (
    <div className="notification-panel-container" ref={triggerRef}>
      {/* Bell Button */}
      <button 
        className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <ResponsivePanelPortal isMobile={isMobile}>
          <div className="notification-panel" ref={panelRef} role="dialog" aria-label="Notifications">
            <div className="notification-panel__header">
              <h3>Notifications</h3>
              <div className="notification-panel__header-actions">
                {unreadCount > 0 && (
                  <button 
                    className="mark-all-read-btn"
                    onClick={handleMarkAllAsRead}
                  >
                    <FaCheckDouble /> Mark all read
                  </button>
                )}

                <button
                  className="close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close notifications"
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

          {/* <div className="notification-panel__filters">
            <button 
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button 
              className={filter === 'announcement' ? 'active' : ''}
              onClick={() => setFilter('announcement')}
            >
              Announcements
            </button>
            <button 
              className={filter === 'system' ? 'active' : ''}
              onClick={() => setFilter('system')}
            >
              System
            </button>
          </div> */}

          <div className="notification-panel__list">
            {isLoading ? (
              <div className="notification-loading">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <FaBell />
                <p>No notifications yet</p>
              </div>
            ) : (
              orderedNotifications.map(notification => {
                const isClickable = Boolean(notification.actionUrl || notification.referenceId);
                return (
                <div 
                  key={notification._id}
                  className={`notification-item ${notification.read ? 'read' : 'unread'} ${isClickable ? 'clickable' : ''}`}
                  onClick={() => handleItemClick(notification)}
                >
                  <div className={`notification-item__icon type-${notification.type}`}>
                    {getIcon(notification)}
                  </div>
                  <div className="notification-item__content">
                    <h4>{notification.title}</h4>
                    <p>{notification.message}</p>
                    <span className="notification-item__time">
                      {formatTime(notification.createdAt)}
                    </span>
                  </div>
                  <div className="notification-item__actions">
                    {!notification.read && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification._id);
                        }}
                        title="Mark as read"
                      >
                        <FaCheck />
                      </button>
                    )}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDismiss(notification._id);
                      }}
                      title="Dismiss"
                      className="dismiss-btn"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-panel__footer">
              <button
                className="clear-all-btn"
                onClick={handleClearAll}
                disabled={isClearing}
                title="Clear all notifications"
              >
                <FaTrash /> Clear all
              </button>
              <button onClick={() => setIsOpen(false)}>
                Close
              </button>
            </div>
          )}
          </div>
        </ResponsivePanelPortal>
      )}

      {/* Announcement detail is now shown via the global <AnnouncementModal />
          (same modal as fresh announcements) through the ui:open-announcement
          event — no inline dialog here so it can't get clipped by the navbar. */}
    </div>
  );
};

export default NotificationPanel;
